import {
    defaultDisplayConfigs,
    type CrmDisplayConfigAddress,
    type CrmDisplayConfigField,
} from '@plato/data/model/crm-config';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { CopyButton } from '@plato/ui-legacy/components/ui/buttons/copy-button';
import type { ColumnDef } from '@plato/ui/components/data-table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@plato/ui/containers/dropdown-menu';
import { MenuItemLabel, MenuItemLeading } from '@plato/ui/primitives/menu';
import { EllipsisVerticalIcon, Pencil } from 'lucide-react';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleMapsLink } from '../../../components/common/google-maps-link.tsx';
import { getFullAddressString } from '../../../lib/address-helper.ts';
import type { CustomerAddressGetDetailOutput } from '../../../server/api/routers/plato-user/query/customer-address.ts';
import { safeGetFieldByPath } from '../../crm/components/crm-context.tsx';
import { FormatValueWithDisplayConfig } from '../../crm/components/crm-field.tsx';
import { EditAddressDialog } from '../../crm/components/edit-address-dialog.tsx';
import { getCrmFieldLabel } from '../../crm/utils/crm-field-labels.ts';

const PresetAddressField = '/_preset/address/formatted';

export type UseCustomerAddressesColumnsProps = {
    config?: CrmDisplayConfigAddress;
};

export function useCustomerAddressesColumns({
    config = defaultDisplayConfigs.address,
}: UseCustomerAddressesColumnsProps) {
    const { t } = useTranslation('customer-detail');

    const customerAddressesColumns: ColumnDef<string, CustomerAddressGetDetailOutput>[] = useMemo(
        () => [
            ...(config.listFields ?? config.fields ?? []).map(
                (field): ColumnDef<string, CustomerAddressGetDetailOutput> => {
                    return {
                        id: field.path,
                        size: field.path === PresetAddressField ? 200 : 60,
                        header: getCrmFieldLabel(t, 'address', field.path) ?? '',
                        cell: ({ row }) => <CustomerAddressColumn field={field} row={row.original} />,
                    };
                },
            ),
            {
                id: 'actions',
                cell: ({ row }) => (
                    <div className="flex items-center justify-end">
                        <CopyButton
                            textToCopy={getFullAddressString(row.original, undefined, '\n')}
                            label={t('action__copy_address', { ns: 'company-address-table' })}
                        />
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                render={<Button variant="ghost" icon={EllipsisVerticalIcon} />}
                            />
                            <DropdownMenuContent align="end" side="bottom">
                                <DropdownMenuItem
                                    className="text-neutral-700"
                                    onClick={() => {
                                        void DialogStateManager.show(EditAddressDialog, {
                                            customerId: row.original.customerId,
                                            addressId: row.original.addressId,
                                        });
                                    }}
                                >
                                    <MenuItemLeading>
                                        <Pencil />
                                    </MenuItemLeading>
                                    <MenuItemLabel>
                                        {t('action__edit', { ns: 'company-address-table' })}
                                    </MenuItemLabel>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                ),
                meta: {
                    align: 'right',
                    pinned: 'right',
                },
                size: 1,
            },
        ],
        [t, config],
    );

    return customerAddressesColumns;
}

type CustomerAddressColumnProps = {
    field: CrmDisplayConfigField;
    row: CustomerAddressGetDetailOutput;
};

function CustomerAddressColumn({ field, row }: CustomerAddressColumnProps): ReactElement {
    if (field.path === PresetAddressField) {
        return (
            <div className="flex items-start gap-2 whitespace-pre py-2">
                <span className="flex-1">{getFullAddressString(row, undefined, '\n')}</span>
                <GoogleMapsLink address={row} className="shrink-0" />
            </div>
        );
    }
    return <FormatValueWithDisplayConfig config={field} value={safeGetFieldByPath(row, field.path)} />;
}
